/* tslint:disable:object-literal-sort-keys variable-name */

import _ from 'lodash';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import LoadingPage from '../../../LoadingPage';
import { Api } from '../../typings/Api';
import { ICustomerParams } from '../../typings/CustomerParameters';
import Model, { IShipToAddress } from '../../model/ShopKeeper.model';

interface IProps {
  container: Object;
  hide: Function;
  model: Model;
  show: boolean;
}

interface IShipToAccountDetailsMap {
  [account: string]: IShipToAddress;
}

interface IShipToSelection {
  type: 'account' | 'address' | 'existing'; // ship-to account, free form address, or what's already set
  index?: number; // paired with type
}

const ConfirmationShipToSelectionModal: FunctionComponent<IProps> = ({ container, hide, model, show }) => {
  // Ship-to accounts need display details backfilled from ES or CUSTPARAMS (we'll use ES for speed)
  const [detailsLoaded, setDetailsLoaded] = useState(
    () => model.lastConfirmationSaleShipToData.shipToAccounts.length === 0
  );
  const [shipToAccountDetailsMap, setShipToAccountDetailsMap] = useState<IShipToAccountDetailsMap>({});
  const [activeSelection, setActiveSelection] = useState<IShipToSelection>({ type: 'existing' });

  useEffect(() => {
    // Backfill ship to account details from ES for speed - this is how saved lists work
    const loadShipToAccountDetails = async () => {
      const detailsMap: IShipToAccountDetailsMap = {};

      for (const shipToAccount of model.lastConfirmationSaleShipToData.shipToAccounts) {
        const id = shipToAccount.account;
        let resEs;

        try {
          resEs = await Api.post('/shopkeeper/esshiptodetail', { id });
          if (resEs.status !== 200) {
            throw new Error('200 not received from esshiptodetail');
          }
          if (Array.isArray(resEs.data) && resEs.data.length >= 1) {
            // This should be a result set of 1 on match, but just in case ...
            const shipToDetails = _.find(resEs.data, o => o.itemid === id);
            if (shipToDetails) {
              // Phone numbers seem to be stored in ES directly as NPANXXXXXX,
              // we want (NPA) NXX-XXXX
              let phone = (shipToDetails.Phone || '').match(/^(\d{3})(\d{3})(\d{4})$/);
              phone = phone ? `(${phone[1]}) ${phone[2]}-${phone[3]}` : shipToDetails.Phone || '';

              detailsMap[shipToAccount.account] = {
                address: shipToDetails.Address || '',
                city: shipToDetails.City || '',
                name: shipToDetails.Name || '',
                phone,
                state: shipToDetails.State || '',
                zipCode: shipToDetails.Zip || '',
              };
            }
          } else {
            throw new Error(`${id} not found in Elasticsearch.`);
          }
        } catch (err: any) {
          // This is not a fatal operation since we're only backfilling display details.
          console.error(err.message);
        }
      }

      setShipToAccountDetailsMap(detailsMap);
      setDetailsLoaded(true);
    };

    if (!detailsLoaded) {
      loadShipToAccountDetails();
    }
  }, []);

  const apply = () => {
    switch (activeSelection.type) {
      case 'existing':
        /* NO-OP */
        break;
      case 'account':
        const account = model.lastConfirmationSaleShipToData.shipToAccounts[activeSelection.index!];
        const details = shipToAccountDetailsMap[account.account]; // Should always be an ES hit, but don't assume

        runInAction(() => {
          model.shipToAddress = null;

          // TODO: Backfill the full ICustomerParams data structure from PCU2
          // This is currentlly a forced cast from partial ES detail (but fully sufficient to operate)
          model.shipToAccount = {
            customerID: account.account,
            name1: details ? details.name : '',
            name2: '', // Unavailable via ES - available in PCU2
            name3: '', // Unavailable via ES - available in PCU2
            address: details ? details.address : '',
            city: details ? details.city : '',
            email: '', // Unavailable via ES - available in PCU2
            phone: details ? details.phone : '',
            state: details ? details.state : '',
            zipcode: details ? details.zipCode : '',
          } as { customerID: string } & ICustomerParams; // cast to intersection to hush TS
        });

        break;
      case 'address':
        runInAction(() => {
          model.shipToAccount = null;
          model.shipToAddress = _.omit(
            model.lastConfirmationSaleShipToData.shipToAddresses[activeSelection.index!],
            'confirmations'
          );
        });
        break;
      default:
        /* NO-OP */
        break;
    }

    hide();
    // Depending on the route type, we may need to apply revised destination tax rates
    model.updateTaxes();
  };

  const renderExisting = () => {
    return (
      <div className="sk-selectionTypeContainer">
        <span className="sk-selectionTypeHeader">Existing Ship-To Variance on Invoice Screen</span>
        <div
          className={classNames({ 'sk-selection-item': true, 'sk-selected': activeSelection.type === 'existing' })}
          onClick={() => setActiveSelection({ type: 'existing' })}
        >
          {model.shipToAccount ? (
            <>
              Ship To Account: {model.shipToAccount.customerID}
              <br />
              {model.shipToAccount.name1}
              <br />
              {model.shipToAccount.address}
              <br />
              {model.shipToAccount.city}, {model.shipToAccount.state} {model.shipToAccount.zipcode}
              <br />
              {model.shipToAccount.phone}
            </>
          ) : model.shipToAddress ? (
            <>
              Ship To Free-Form Address:
              <br />
              {model.shipToAddress.name}
              <br />
              {model.shipToAddress.address}
              <br />
              {model.shipToAddress.city}, {model.shipToAddress.state} {model.shipToAddress.zipCode}
              <br />
              {model.shipToAddress.phone}
            </>
          ) : (
            <span>No Ship To Variance Set (Use Default Ship To Address On File)</span>
          )}
        </div>
      </div>
    );
  };

  const renderShipToAccounts = () => {
    if (model.lastConfirmationSaleShipToData.shipToAccounts.length === 0) {
      return null;
    } else {
      return (
        <div className="sk-selectionTypeContainer">
          <span className="sk-selectionTypeHeader">Ship-To Accounts Encountered</span>
          {model.lastConfirmationSaleShipToData.shipToAccounts.map((account, index) => {
            const details = shipToAccountDetailsMap[account.account];
            return (
              <div
                className={classNames({
                  'sk-selected': activeSelection.type === 'account' && activeSelection.index === index,
                  'sk-selection-item': true,
                })}
                onClick={() => setActiveSelection({ type: 'account', index })}
                key={`shipToAccount${index}`}
              >
                <span style={{ textDecoration: 'underline' }}>Confirmations: {account.confirmations.join(', ')}</span>
                <br />
                Ship To Account: {account.account}
                <br />
                {details && (
                  <>
                    {details.name}
                    <br />
                    {details.address}
                    <br />
                    {details.city}, {details.state} {details.zipCode}
                    <br />
                    {details.phone}
                  </>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  };

  const renderShipToAddresses = () => {
    if (model.lastConfirmationSaleShipToData.shipToAddresses.length === 0) {
      return null;
    } else {
      return (
        <div className="sk-selectionTypeContainer">
          <span className="sk-selectionTypeHeader">Ship-To Free-Form Addresses Encountered</span>
          {model.lastConfirmationSaleShipToData.shipToAddresses.map((details, index) => {
            return (
              <div
                className={classNames({
                  'sk-selected': activeSelection.type === 'address' && activeSelection.index === index,
                  'sk-selection-item': true,
                })}
                onClick={() => setActiveSelection({ type: 'address', index })}
                key={`shipToAddress${index}`}
              >
                <span style={{ textDecoration: 'underline' }}>Confirmations: {details.confirmations.join(', ')}</span>
                <br />
                {details.name}
                <br />
                {details.address}
                <br />
                {details.city}, {details.state} {details.zipCode}
                <br />
                {details.phone}
              </div>
            );
          })}
        </div>
      );
    }
  };

  return (
    <Modal
      backdrop="static"
      container={container as any}
      dialogClassName="sk-confirmationShipToSelectionModal"
      onHide={hide as any}
      show={show}
    >
      <Modal.Header closeButton={true}>
        <Modal.Title>Ship To Selection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detailsLoaded ? (
          <>
            Additional ship to information was encountered when adding line items. Please select.
            <br />
            {renderExisting()}
            {renderShipToAccounts()}
            {renderShipToAddresses()}
          </>
        ) : (
          <LoadingPage />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={() => apply()}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationShipToSelectionModal;
